import { useAccountState } from '../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Button, Field, makeStyles, ProgressBar } from '@fluentui/react-components';
import { Dismiss16Regular } from '@fluentui/react-icons';
import { CustomCardHeader } from './CustomCardHeader';
import { useCommonStyles } from './Styles/commonStyles';
import { Column } from './Column';
import { Row } from './Row';
import { FileUploadManager } from '../../data/services/FileUploadManager';

import { useUploadState } from '../../redux/reducers/uploadReducer';
import { useUploadActions } from '../../redux/actions/uploadActions';
import { appTokens } from './Styles/appTokens';

const useStyles = makeStyles({
    panel: {
        width: '24rem',
        maxWidth: 'calc(100vw - 2.5rem)',
        height: 'auto',
        backgroundColor: appTokens.card.background,
        boxShadow: appTokens.shadow.shadow16,
        overflow: 'hidden',
    },
    field: {
        paddingBlock: '.5rem',
    },
});

export const UploadDocumentsControl = () => {
    //Objects
    const fileUploadManager: FileUploadManager = FileUploadManager.getInstance();

    //Hooks
    const { portalAccount } = useAccountState();
    const uploadState = useUploadState();
    const uploadActions = useUploadActions();

    const [isOpen, setIsOpen] = useState<boolean>(true);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //UI Events
    const clickClose = (e: any) => {
        e.stopPropagation();

        fileUploadManager.clearUploadedFiles();
    };
    const clickRemoveUpload = (e: any, fileId: string) => {
        e.stopPropagation();

        fileUploadManager.clearUpload(fileId);
    };

    //Logic
    const formatTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        return `${hours}h ${minutes}m ${secs}s`;
    };

    //Effects
    useEffect(() => {
        fileUploadManager.setAccount(portalAccount?.accountId || '');
    }, [portalAccount?.accountId]);

    useEffect(() => {
        fileUploadManager.setUploadActions(uploadActions);
    }, [!!uploadActions]);

    useEffect(() => {
        setIsOpen(!!uploadState?.fileUploads.length);
    }, [uploadState?.fileUploads.length]);

    return (
        <>
            {!isOpen ? (
                <></>
            ) : (
                <Column className={styles.panel} height='auto' noGap subtleBorder borderRadius='10px'>
                    <CustomCardHeader
                        title={`Uploading ${uploadState?.fileUploads.length} ${uploadState?.fileUploads.length > 1 ? 'Files' : 'File'}`}
                        titleWeight='semibold'
                        padding='px-3 py-2'
                        border
                        action={
                            <Button
                                size='small'
                                className={commonStyles.button.root}
                                appearance='subtle'
                                icon={<Dismiss16Regular />}
                                onClick={clickClose}
                                data-testid='upload-documents-control-close-button'
                            />
                        }
                    />
                    <Column padding='px-3 py-3' gap='small'>
                        {uploadState?.fileUploads.map((fileUploadInfo) => {
                            const progress = Math.min(1, Math.max(0, fileUploadInfo.progress));

                            return <Row noWrap key={fileUploadInfo.fileId}>
                                <Column>
                                    <Field
                                        label={fileUploadInfo.fileName}
                                        className={styles.field}
                                        validationMessage={
                                            progress >= 1
                                                ? `Successfully Uploaded${fileUploadInfo.uploadTime ? ` - (in ${formatTime(fileUploadInfo.uploadTime)})` : ''}`
                                                : undefined
                                        }
                                        validationState={progress >= 1 ? 'success' : undefined}
                                    >
                                        <ProgressBar
                                            style={{ width: '300px' }}
                                            value={progress}
                                            color={progress >= 1 ? 'success' : 'brand'}
                                        />
                                    </Field>
                                </Column>
                                <Column width='auto'>
                                    {progress == 0 && (
                                        <Button
                                            className={commonStyles.button.root}
                                            appearance='subtle'
                                            icon={<Dismiss16Regular />}
                                            onClick={(e: any) => clickRemoveUpload(e, fileUploadInfo.fileId)}
                                            data-testid={`upload-documents-control-remove-button-${fileUploadInfo.fileId}`}
                                        />
                                    )}
                                </Column>
                            </Row>
                        })}
                    </Column>
                </Column>
            )}
        </>
    );
};
